/**
 * Google Drive REST v3 over plain `fetch`. No gapi client, no broader scope.
 * https://developers.google.com/workspace/drive/api/reference/rest/v3
 *
 * Every call takes the token from `getSilentToken()`, so nothing here ever
 * opens a Google dialog: when no token can be had the call fails with a
 * `DriveError` of code `needs_sign_in` and the UI offers [Logga in].
 *
 * Retries: 429 and rate-limit 403s, 5xx and network failures back off
 * exponentially (with jitter, honouring Retry-After). A 401 invalidates the
 * cached token and is retried once with a fresh silent token.
 *
 * The dependencies (fetch, token, sleep) are injectable for tests.
 */
import { RemoteStoreError, codeForStatus, type RemoteErrorCode } from '@/cloud/store';
import { getSilentToken, invalidateToken } from './auth';

/** Provider name in Swedish error sentences. */
export const DRIVE_LABEL = 'Google Drive';

export const API = 'https://www.googleapis.com/drive/v3';
export const UPLOAD = 'https://www.googleapis.com/upload/drive/v3';
export const FOLDER_MIME = 'application/vnd.google-apps.folder';

/** Multipart limit per Google's upload guide; larger files go resumable. */
export const MULTIPART_LIMIT = 5 * 1024 * 1024;
/** Resumable chunk size, a multiple of 256 KiB as required. */
export const RESUMABLE_CHUNK = 8 * 1024 * 1024;
/** appProperties limits (private per app). */
export const APP_PROPERTY_MAX_KEYS = 30;
export const APP_PROPERTY_MAX_BYTES = 124;

export const FILE_FIELDS = 'id,name,mimeType,modifiedTime,createdTime,size,parents,md5Checksum,appProperties,trashed';

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  createdTime?: string;
  /** Drive returns size as a decimal string. */
  size?: string;
  parents?: string[];
  md5Checksum?: string;
  appProperties?: Record<string, string>;
  trashed?: boolean;
}

/** The port's codes; kept as a Drive-flavoured alias for existing call sites. */
export type DriveErrorCode = RemoteErrorCode;

/**
 * Drive's own wording, overriding the port's generic sentences. `forbidden`
 * explains the drive.file scope, which is the answer the user actually needs
 * when Drive refuses a file another app created.
 */
const DRIVE_MESSAGES: Partial<Record<DriveErrorCode, string>> = {
  needs_sign_in: 'Google-inloggning krävs. Logga in igen för att fortsätta.',
  forbidden: 'Google Drive nekade åtkomst. Appen kan bara nå filer den själv har skapat.',
  not_found: 'Filen finns inte längre på Google Drive.',
  rate_limited: 'Google Drive är tillfälligt upptaget. Försök igen om en stund.',
  conflict: 'Filen ändrades på Google Drive under tiden. Försök igen.',
  bad_request: 'Google Drive förstod inte begäran.',
  network: 'Ingen kontakt med Google Drive. Kontrollera nätverket och försök igen.',
  server: 'Google Drive svarade med ett fel. Försök igen om en stund.',
  other: 'Något gick fel mot Google Drive.',
};

/**
 * A `RemoteStoreError` carrying Drive's wording. Policy code above the port
 * catches it as `isRemoteError(e, 'needs_sign_in')`; Drive-only code may still
 * use `isDriveError`.
 */
export class DriveError extends RemoteStoreError {
  constructor(status: number, technical?: string, code?: DriveErrorCode, retryAfterMs?: number) {
    super({ status, provider: DRIVE_LABEL, messages: DRIVE_MESSAGES, code, technical, retryAfterMs });
    this.name = 'DriveError';
  }
}

export { codeForStatus };

export function isDriveError(e: unknown, code?: DriveErrorCode): e is DriveError {
  return e instanceof DriveError && (code === undefined || e.code === code);
}

export interface DriveClientDeps {
  fetch: typeof fetch;
  /** Null means "no token without asking the user". */
  getToken: () => Promise<string | null>;
  invalidateToken: () => void;
  sleep: (ms: number) => Promise<void>;
  random: () => number;
  maxAttempts: number;
  /** Uploads above this go resumable (default 5 MB). */
  multipartLimit: number;
  /** Resumable chunk size, a multiple of 256 KiB (default 8 MiB). */
  chunkSize: number;
}

function defaultDeps(): DriveClientDeps {
  return {
    fetch: (input, init) => fetch(input, init),
    getToken: getSilentToken,
    invalidateToken,
    sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
    random: Math.random,
    maxAttempts: 5,
    multipartLimit: MULTIPART_LIMIT,
    chunkSize: RESUMABLE_CHUNK,
  };
}

let deps: DriveClientDeps = defaultDeps();

/** Swap fetch/token/sleep (tests). Partial overrides merge onto the defaults. */
export function configureDriveClient(partial: Partial<DriveClientDeps>): void {
  deps = { ...defaultDeps(), ...partial };
}

export function resetDriveClient(): void {
  deps = defaultDeps();
}

const RATE_LIMIT_REASONS = new Set([
  'userRateLimitExceeded',
  'rateLimitExceeded',
  'dailyLimitExceeded',
  'quotaExceeded',
  'sharingRateLimitExceeded',
]);

interface ErrorBody {
  error?: { message?: string; errors?: { reason?: string; message?: string }[] };
}

async function toDriveError(res: Response): Promise<DriveError> {
  let technical = `HTTP ${res.status} ${res.statusText}`.trim();
  let rateLimited = res.status === 429;
  try {
    const body = (await res.json()) as ErrorBody;
    const reasons = body.error?.errors?.map((e) => e.reason).filter((r): r is string => !!r) ?? [];
    if (body.error?.message) technical = `HTTP ${res.status}: ${body.error.message}`;
    if (reasons.length) technical += ` (${reasons.join(', ')})`;
    if (res.status === 403 && reasons.some((r) => RATE_LIMIT_REASONS.has(r))) rateLimited = true;
  } catch {
    // Body was not JSON; keep the status line.
  }
  const retryAfter = parseRetryAfter(res.headers.get('Retry-After'));
  const code = rateLimited ? 'rate_limited' : codeForStatus(res.status);
  return new DriveError(res.status, technical, code, retryAfter);
}

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const at = Date.parse(header);
  return Number.isNaN(at) ? undefined : Math.max(0, at - Date.now());
}

/** Exponential backoff with full jitter: 1 s, 2 s, 4 s, 8 s … capped at 32 s. */
export function backoffDelay(attempt: number, random: () => number = Math.random, retryAfterMs?: number): number {
  if (retryAfterMs !== undefined) return retryAfterMs;
  const base = Math.min(32_000, 1000 * 2 ** attempt);
  return Math.round(base / 2 + random() * (base / 2));
}

function shouldRetry(error: DriveError): boolean {
  return error.code === 'rate_limited' || error.code === 'server' || error.code === 'network';
}

/**
 * Authorised fetch with retries. Resolves with an OK response; every other
 * outcome is a `DriveError`. `expectStatuses` lets callers accept e.g. 308.
 */
export async function driveFetch(url: string, init: RequestInit = {}, opts: { expectStatuses?: number[]; auth?: boolean } = {}): Promise<Response> {
  const auth = opts.auth ?? true;
  let refreshedOnce = false;
  for (let attempt = 0; ; attempt++) {
    const headers = new Headers(init.headers);
    if (auth) {
      const token = await deps.getToken();
      if (!token) throw new DriveError(401, 'no token available without user interaction');
      headers.set('Authorization', `Bearer ${token}`);
    }
    let res: Response;
    try {
      res = await deps.fetch(url, { ...init, headers });
    } catch (e) {
      const err = new DriveError(0, e instanceof Error ? e.message : String(e), 'network');
      if (attempt + 1 >= deps.maxAttempts) throw err;
      await deps.sleep(backoffDelay(attempt, deps.random));
      continue;
    }
    if (res.ok || opts.expectStatuses?.includes(res.status)) return res;
    const err = await toDriveError(res);
    if (err.status === 401 && auth && !refreshedOnce) {
      refreshedOnce = true;
      deps.invalidateToken();
      continue;
    }
    if (shouldRetry(err) && attempt + 1 < deps.maxAttempts) {
      await deps.sleep(backoffDelay(attempt, deps.random, err.retryAfterMs));
      continue;
    }
    throw err;
  }
}

/** Escape a value for use inside single quotes in a Drive `q` expression. */
export function escapeQuery(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function listFields(fields: string): string {
  const inner = fields.includes('files(') ? fields : `files(${fields})`;
  return inner.includes('nextPageToken') ? inner : `nextPageToken,${inner}`;
}

/** `files.list` following `nextPageToken` until the end. */
export async function listFilesAll(query: string, fields: string = FILE_FIELDS): Promise<DriveFile[]> {
  const out: DriveFile[] = [];
  let pageToken: string | undefined;
  do {
    const params = new URLSearchParams({ q: query, fields: listFields(fields), spaces: 'drive', pageSize: '100' });
    if (pageToken) params.set('pageToken', pageToken);
    const res = await driveFetch(`${API}/files?${params}`);
    const body = (await res.json()) as { files?: DriveFile[]; nextPageToken?: string };
    out.push(...(body.files ?? []));
    pageToken = body.nextPageToken;
  } while (pageToken);
  return out;
}

/** @deprecated use `listFilesAll`; kept for existing callers. */
export const listFiles = listFilesAll;

export async function getFileMeta(fileId: string, fields: string = FILE_FIELDS): Promise<DriveFile> {
  const res = await driveFetch(`${API}/files/${encodeURIComponent(fileId)}?fields=${encodeURIComponent(fields)}`);
  return (await res.json()) as DriveFile;
}

/** `files.get` that answers "is it still there?" instead of throwing on 404. */
export async function fileExists(fileId: string): Promise<boolean> {
  try {
    const meta = await getFileMeta(fileId, 'id,trashed');
    return !meta.trashed;
  } catch (e) {
    if (isDriveError(e, 'not_found')) return false;
    throw e;
  }
}

export async function createFolder(name: string, parentId?: string): Promise<DriveFile> {
  const res = await driveFetch(`${API}/files?fields=${encodeURIComponent(FILE_FIELDS)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, mimeType: FOLDER_MIME, ...(parentId ? { parents: [parentId] } : {}) }),
  });
  return (await res.json()) as DriveFile;
}

/** Find (by name, not trashed) or create a folder, optionally inside a parent. */
export async function ensureFolder(name: string, parentId?: string): Promise<DriveFile> {
  const existing = await findByName(name, parentId, FOLDER_MIME);
  if (existing) return existing;
  return createFolder(name, parentId);
}

/** Oldest matching file by name inside a parent (or anywhere the app can see). */
export async function findByName(name: string, parentId?: string, mimeType?: string): Promise<DriveFile | undefined> {
  const clauses = [`name = '${escapeQuery(name)}'`, 'trashed = false'];
  if (parentId) clauses.push(`'${escapeQuery(parentId)}' in parents`);
  if (mimeType) clauses.push(`mimeType = '${mimeType}'`);
  const files = await listFilesAll(clauses.join(' and '));
  files.sort((a, b) => (a.createdTime ?? '').localeCompare(b.createdTime ?? ''));
  return files[0];
}

export interface UploadInput {
  name: string;
  parentId: string;
  blob: Blob;
  appProperties?: Record<string, string>;
  /** Overwrite this file's content (PATCH) instead of creating a new one. */
  fileId?: string;
  /** Defaults to `blob.type` or application/octet-stream. */
  mediaType?: string;
}

function metadataFor(input: UploadInput): Record<string, unknown> {
  const metadata: Record<string, unknown> = { name: input.name };
  if (!input.fileId) metadata.parents = [input.parentId];
  if (input.appProperties) {
    validateAppProperties(input.appProperties);
    metadata.appProperties = input.appProperties;
  }
  return metadata;
}

/**
 * Upload a blob: multipart for ≤ 5 MB, resumable above. With `fileId` the
 * existing file's content and name are replaced (parents unchanged).
 */
export async function uploadBlob(input: UploadInput): Promise<DriveFile> {
  return input.blob.size <= deps.multipartLimit ? uploadMultipart(input) : uploadResumable(input);
}

async function uploadMultipart(input: UploadInput): Promise<DriveFile> {
  const mediaType = input.mediaType ?? input.blob.type ?? 'application/octet-stream';
  const boundary = `boundary_${crypto.randomUUID()}`;
  const head =
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadataFor(input))}\r\n` +
    `--${boundary}\r\nContent-Type: ${mediaType || 'application/octet-stream'}\r\n\r\n`;
  const body = new Blob([head, input.blob, `\r\n--${boundary}--`]);
  const base = input.fileId ? `${UPLOAD}/files/${encodeURIComponent(input.fileId)}` : `${UPLOAD}/files`;
  const res = await driveFetch(`${base}?uploadType=multipart&fields=${encodeURIComponent(FILE_FIELDS)}`, {
    method: input.fileId ? 'PATCH' : 'POST',
    headers: { 'Content-Type': `multipart/related; boundary=${boundary}` },
    body,
  });
  return (await res.json()) as DriveFile;
}

/** Parse `Range: bytes=0-262143` into the next offset to send. */
export function nextOffsetFromRange(range: string | null, fallback: number): number {
  if (!range) return fallback;
  const m = /bytes=\d+-(\d+)/.exec(range);
  return m ? Number(m[1]) + 1 : fallback;
}

/** Build the session URI when `Location` is hidden by CORS but the upload id is exposed. */
export function sessionUriFromUploadId(base: string, uploadId: string): string {
  return `${base}?uploadType=resumable&upload_id=${encodeURIComponent(uploadId)}`;
}

async function uploadResumable(input: UploadInput): Promise<DriveFile> {
  const mediaType = input.mediaType ?? input.blob.type ?? 'application/octet-stream';
  const size = input.blob.size;
  const base = input.fileId ? `${UPLOAD}/files/${encodeURIComponent(input.fileId)}` : `${UPLOAD}/files`;
  const init = await driveFetch(`${base}?uploadType=resumable&fields=${encodeURIComponent(FILE_FIELDS)}`, {
    method: input.fileId ? 'PATCH' : 'POST',
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Upload-Content-Type': mediaType || 'application/octet-stream',
      'X-Upload-Content-Length': String(size),
    },
    body: JSON.stringify(metadataFor(input)),
  });
  let session = init.headers.get('Location');
  if (!session) {
    const uploadId = init.headers.get('X-GUploader-UploadID');
    if (!uploadId) throw new DriveError(init.status, 'resumable session started but neither Location nor X-GUploader-UploadID was exposed', 'other');
    session = sessionUriFromUploadId(base, uploadId);
  }

  let offset = 0;
  while (offset < size) {
    const end = Math.min(offset + deps.chunkSize, size);
    let res: Response;
    try {
      res = await driveFetch(
        session,
        {
          method: 'PUT',
          headers: { 'Content-Type': mediaType || 'application/octet-stream', 'Content-Range': `bytes ${offset}-${end - 1}/${size}` },
          body: input.blob.slice(offset, end),
        },
        { expectStatuses: [308] },
      );
    } catch (e) {
      if (!isDriveError(e) || e.code === 'not_found' || e.code === 'needs_sign_in' || e.code === 'forbidden') throw e;
      // Interrupted: ask the session where it is and continue from there.
      const status = await driveFetch(
        session,
        { method: 'PUT', headers: { 'Content-Range': `bytes */${size}` } },
        { expectStatuses: [308] },
      );
      if (status.status !== 308) return (await status.json()) as DriveFile;
      offset = nextOffsetFromRange(status.headers.get('Range'), offset);
      continue;
    }
    if (res.status === 308) {
      // Range may be hidden by CORS; then trust that the whole chunk landed.
      offset = nextOffsetFromRange(res.headers.get('Range'), end);
      continue;
    }
    return (await res.json()) as DriveFile;
  }
  throw new DriveError(0, 'resumable upload ended without a final response', 'other');
}

/** Create or overwrite a JSON file. Returns the file metadata incl. modifiedTime. */
export async function uploadJson(input: { name: string; parentId: string; data: unknown; fileId?: string; appProperties?: Record<string, string> }): Promise<DriveFile> {
  const blob = new Blob([JSON.stringify(input.data)], { type: 'application/json' });
  return uploadBlob({
    name: input.name,
    parentId: input.parentId,
    blob,
    mediaType: 'application/json',
    ...(input.fileId ? { fileId: input.fileId } : {}),
    ...(input.appProperties ? { appProperties: input.appProperties } : {}),
  });
}

/** Move (one parent only) and optionally rename in one PATCH. */
export async function moveFile(fileId: string, from: string, to: string, newName?: string): Promise<DriveFile> {
  const params = new URLSearchParams({ addParents: to, removeParents: from, fields: FILE_FIELDS });
  const res = await driveFetch(`${API}/files/${encodeURIComponent(fileId)}?${params}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newName ? { name: newName } : {}),
  });
  return (await res.json()) as DriveFile;
}

export async function renameFile(fileId: string, newName: string): Promise<DriveFile> {
  const res = await driveFetch(`${API}/files/${encodeURIComponent(fileId)}?fields=${encodeURIComponent(FILE_FIELDS)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: newName }),
  });
  return (await res.json()) as DriveFile;
}

const utf8 = new TextEncoder();

/** Throws a RangeError when Drive would reject the properties. */
export function validateAppProperties(props: Record<string, string | null>): void {
  const keys = Object.keys(props);
  if (keys.length > APP_PROPERTY_MAX_KEYS) throw new RangeError(`appProperties: at most ${APP_PROPERTY_MAX_KEYS} keys (got ${keys.length})`);
  for (const key of keys) {
    const value = props[key];
    const bytes = utf8.encode(key).length + (value === null ? 0 : utf8.encode(value).length);
    if (bytes > APP_PROPERTY_MAX_BYTES) throw new RangeError(`appProperties: "${key}" is ${bytes} bytes; key + value must be ≤ ${APP_PROPERTY_MAX_BYTES}`);
  }
}

/** Merge app properties; a null value deletes the key. */
export async function patchAppProperties(fileId: string, props: Record<string, string | null>): Promise<void> {
  validateAppProperties(props);
  await driveFetch(`${API}/files/${encodeURIComponent(fileId)}?fields=id`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ appProperties: props }),
  });
}

/** Download file content (`alt=media`). Never use `thumbnailLink`. */
export async function downloadBlob(fileId: string): Promise<Blob> {
  const res = await driveFetch(`${API}/files/${encodeURIComponent(fileId)}?alt=media`);
  return res.blob();
}

export async function downloadJson<T = unknown>(fileId: string): Promise<T> {
  const res = await driveFetch(`${API}/files/${encodeURIComponent(fileId)}?alt=media`);
  return (await res.json()) as T;
}

export async function copyFile(fileId: string, target: { name: string; parentId: string; appProperties?: Record<string, string> }): Promise<DriveFile> {
  if (target.appProperties) validateAppProperties(target.appProperties);
  const res = await driveFetch(`${API}/files/${encodeURIComponent(fileId)}/copy?fields=${encodeURIComponent(FILE_FIELDS)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: target.name, parents: [target.parentId], ...(target.appProperties ? { appProperties: target.appProperties } : {}) }),
  });
  return (await res.json()) as DriveFile;
}

/**
 * Move a file to the trash. The app never calls this for bookkeeping records
 * (BFL 7 kap.); it exists for cleaning up failed partial uploads only.
 */
export async function trashFile(fileId: string): Promise<void> {
  await driveFetch(`${API}/files/${encodeURIComponent(fileId)}?fields=id`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ trashed: true }),
  });
}

/** Web link for "Öppna i Drive". */
export function driveFolderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${encodeURIComponent(folderId)}`;
}
